import { KeyedOption, Option } from './historyOptions'

// A `path` list takes the first one present, for keys that were stored under two names.
export type Path = string | string[]

// `input` only matters to the logger form. `when` hides a field there and in the history view.
type FieldBase = { when?: (value: Record<string, unknown>) => boolean; hint?: string; placeholder?: string }

export type Field = FieldBase &
  (
    | { kind: 'text' | 'yesno' | 'date' | 'codes'; label: string; path: Path }
    | { kind: 'value'; label: string; path: Path; input?: 'datetime' | 'number' }
    | { kind: 'tags'; label: string; path: Path; nameFor: (code: string, source: Value) => string }
    | { kind: 'metrics'; label: string; path: Path }
    | { kind: 'rows'; label: string; path: Path; columns: readonly KeyedOption[] }
    | { kind: 'choice'; label: string; path: Path; options: readonly Option[] }
    | { kind: 'checks'; label: string; path: Path; options: readonly KeyedOption[]; otherLabel?: string }
  )

export type Value = Record<string, unknown> | undefined

export type Section = {
  title?: string
  fields: Field[]
  readOnly?: boolean
  when?: (value: Record<string, unknown>) => boolean
}

export const text = (label: string, path: Path): Field => ({ kind: 'text', label, path })
export const value = (label: string, path: Path, input?: 'datetime' | 'number'): Field => ({
  kind: 'value',
  label,
  path,
  input,
})
export const withHint = (field: Field, extra: { hint?: string; placeholder?: string }): Field => ({
  ...field,
  ...extra,
})
export const showWhen = (when: NonNullable<Field['when']>, field: Field): Field => ({ ...field, when })
export const yesno = (label: string, path: Path): Field => ({ kind: 'yesno', label, path })
export const codes = (label: string, path: Path): Field => ({ kind: 'codes', label, path })
export const date = (label: string, path: Path): Field => ({ kind: 'date', label, path })
export const choice = (label: string, path: Path, options: readonly Option[]): Field => ({
  kind: 'choice',
  label,
  path,
  options,
})
export const checks = (label: string, path: Path, options: readonly KeyedOption[], otherLabel?: string): Field => ({
  kind: 'checks',
  label,
  path,
  options,
  otherLabel,
})
export const tags = (label: string, path: Path, nameFor: (code: string, source: Value) => string): Field => ({
  kind: 'tags',
  label,
  path,
  nameFor,
})
export const rows = (label: string, path: Path, columns: readonly KeyedOption[]): Field => ({
  kind: 'rows',
  label,
  path,
  columns,
})
export const metrics = (label: string, path: Path): Field => ({ kind: 'metrics', label, path })
export const textsOf = (fields: readonly (readonly [string, string])[], prefix = ''): Field[] =>
  fields.map(([key, label]) => text(label, `${prefix}${key}`))

export const YES_NO: Option[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
]
